import { documentService, type DocumentDto } from "../../../services/documentService";
import { baueZip, eindeutigeNamen } from "../../../utils/zipArchiv";

/**
 * DOKUMENTE AUS DEM VORGANG HERUNTERLADEN (PO 28.09.2026: „Herunterladen der
 * Dokumente im Vorgang").
 *
 * Bis dahin öffnete ein Klick auf die Dokumentkarte den Betrachter; ein
 * Download stand erst darin, und mehrere Dokumente ließen sich nur einzeln
 * holen. Jetzt:
 *
 *  - EIN Dokument → der gewohnte signierte Download (derselbe Weg wie im
 *    Betrachter, `openSignedDownload`).
 *  - MEHRERE → ein ZIP, im Browser gebündelt. Jede Datei kommt über
 *    `GET documents/:id/download` — also durch dieselbe Leseprüfung wie der
 *    Einzeldownload; ein Sammel-Endpunkt mit eigener Rechteregel wäre eine
 *    zweite Wahrheit.
 *
 * Schlägt eine Datei fehl, bricht das Paket NICHT ab: der Rest kommt ins ZIP,
 * und die Meldung nennt, was fehlt.
 */

/** Wie viele Dateien gleichzeitig geladen werden — schont Server und Leitung. */
const PARALLEL = 3;

export type DownloadErgebnis = { geladen: number; fehlgeschlagen: string[] };

/** „Vorgang V-2026-0042 – Dokumente.zip" — ohne Nummer „Vorgang 42 – …". */
export function zipDateiname(vorgang: { id: number; vorgangNbr?: string | null }): string {
    const kennung = (vorgang.vorgangNbr ?? "").trim() || String(vorgang.id);
    return `Vorgang ${kennung} – Dokumente.zip`.replace(/[\\/:*?"<>|]/g, "_");
}

/** Dieselbe Datei steht als Heimat und als Gast nur einmal im Paket. */
export function ohneDoppelte(dokumente: DocumentDto[]): DocumentDto[] {
    const gesehen = new Set<number>();
    return dokumente.filter((d) => (gesehen.has(d.id) ? false : (gesehen.add(d.id), true)));
}

function speichere(blob: Blob, dateiname: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = dateiname;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Edge bricht den Download ab, wenn die URL sofort verschwindet.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function dokumenteHerunterladen(
    dokumente: DocumentDto[],
    zipName: string,
    onFortschritt?: (fertig: number, gesamt: number) => void,
): Promise<DownloadErgebnis> {
    const liste = ohneDoppelte(dokumente);
    if (liste.length === 0) return { geladen: 0, fehlgeschlagen: [] };
    if (liste.length === 1) {
        await documentService.openSignedDownload(liste[0]!.id);
        return { geladen: 1, fehlgeschlagen: [] };
    }

    const namen = eindeutigeNamen(liste.map((d) => d.fileName));
    const daten: Array<Uint8Array | null> = new Array(liste.length).fill(null);
    const fehlgeschlagen: string[] = [];
    let fertig = 0;
    let naechster = 0;
    const arbeiter = async () => {
        while (naechster < liste.length) {
            const i = naechster++;
            const dok = liste[i]!;
            try {
                const blob = await documentService.fetchBlob(dok.id);
                daten[i] = new Uint8Array(await blob.arrayBuffer());
            } catch (err) {
                console.error("[dokumenteHerunterladen] Datei fehlgeschlagen", { id: dok.id, err });
                fehlgeschlagen.push(dok.fileName || `Dokument ${dok.id}`);
            }
            onFortschritt?.(++fertig, liste.length);
        }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, liste.length) }, arbeiter));

    const eintraege = liste.flatMap((d, i) => {
        const bytes = daten[i];
        if (!bytes) return [];
        const datum = d.uploadedAt ? new Date(d.uploadedAt) : undefined;
        return [
            {
                name: namen[i]!,
                daten: bytes,
                ...(datum && !Number.isNaN(datum.getTime()) ? { datum } : {}),
            },
        ];
    });
    if (eintraege.length > 0) {
        const zip = baueZip(eintraege);
        speichere(new Blob([zip as BlobPart], { type: "application/zip" }), zipName);
    }
    return { geladen: eintraege.length, fehlgeschlagen };
}
